import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  DestroyRef,
  EnvironmentInjector,
  Inject,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { BodyScrollLockService } from '@shared/services';
import { SdBottomSheetOptions } from '@shared/v2/models';
import { filter } from 'rxjs';
import { SdBottomSheetComponent } from './bottom-sheet.component';
import { SD_BOTTOM_SHEET_OPTIONS_TOKEN } from './bottom-sheet.token';

@Injectable({
  providedIn: 'root',
})
export class SdBottomSheetService {
  private bottomSheetRef: ComponentRef<SdBottomSheetComponent<any>> | null = null;
  private contentRef: ComponentRef<any> | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector,
    @Inject(DOCUMENT) private document: Document,
    private destroyRef: DestroyRef,
    private router: Router,
    private bodyScrollLockService: BodyScrollLockService,
  ) {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.close();
      });
  }

  open<T>(content: Type<any>, options: SdBottomSheetOptions<T>): void {
    if (this.bottomSheetRef) {
      this.close();
    }

    this.contentRef = createComponent(content, {
      environmentInjector: this.injector,
    });

    const { data } = options;
    if (data) {
      Object.keys(data).forEach((key) => {
        if (this.contentRef && data) {
          this.contentRef.setInput(key, data[key]);
        }
      });
    }

    const elementInjector = Injector.create({
      providers: [
        {
          provide: SD_BOTTOM_SHEET_OPTIONS_TOKEN,
          useValue: options,
        },
      ],
      parent: this.injector,
    });

    this.bottomSheetRef = createComponent(SdBottomSheetComponent, {
      environmentInjector: this.injector,
      elementInjector,
      projectableNodes: [[this.contentRef.location.nativeElement]],
    });

    this.document.body.appendChild(this.bottomSheetRef.location.nativeElement);
    this.appRef.attachView(this.bottomSheetRef.hostView);
    this.appRef.attachView(this.contentRef.hostView);

    this.bodyScrollLockService.lock();

    this.bottomSheetRef.instance.close$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.close();
    });
  }

  close(): void {
    if (this.bottomSheetRef && this.contentRef) {
      this.appRef.detachView(this.contentRef.hostView);
      this.appRef.detachView(this.bottomSheetRef.hostView);
      this.contentRef.destroy();
      this.bottomSheetRef.destroy();
      this.contentRef = null;
      this.bottomSheetRef = null;
      this.bodyScrollLockService.unlock();
    }
  }
}
